import { useState, useMemo } from "react"
import type { ColumnId, ColumnDef } from "../utils"
import { STORAGE_KEY } from "../utils"

export function useColumnOrder(defaultColumns: ColumnDef[]) {
  const [columnOrder, setColumnOrder] = useState<ColumnId[] | null>(null)
  const [draggingColumnId, setDraggingColumnId] = useState<ColumnId | null>(null)

  const columns = useMemo(() => {
    const orderColumns = (order: ColumnId[]) => {
      const byId = Object.fromEntries(defaultColumns.map((column) => [column.id, column]))
      const ordered = order.map((id) => byId[id]).filter((column): column is ColumnDef => Boolean(column))
      const missing = defaultColumns.filter((column) => !ordered.find((existing) => existing.id === column.id))
      return [...ordered, ...missing]
    }

    if (columnOrder === null) {
      if (typeof window === "undefined") return defaultColumns

      try {
        const stored = window.localStorage.getItem(STORAGE_KEY)
        if (!stored) return defaultColumns
        return orderColumns(JSON.parse(stored) as ColumnId[])
      } catch {
        return defaultColumns
      }
    }

    return orderColumns(columnOrder)
  }, [defaultColumns, columnOrder])

  const handleHeaderDragStart = (event: React.DragEvent, id: ColumnId) => {
    setDraggingColumnId(id)
    event.dataTransfer.effectAllowed = "move"
  }

  const handleHeaderDragOver = (event: React.DragEvent, id: ColumnId) => {
    event.preventDefault()
    if (!draggingColumnId || draggingColumnId === id) return

    setColumnOrder((currentOrder) => {
      const effectiveOrder = currentOrder ?? columns.map((column) => column.id)

      const fromIndex = effectiveOrder.findIndex((columnId) => columnId === draggingColumnId)
      const toIndex = effectiveOrder.findIndex((columnId) => columnId === id)

      if (fromIndex === -1 || toIndex === -1) return effectiveOrder

      const updated = [...effectiveOrder]
      const [moved] = updated.splice(fromIndex, 1)
      updated.splice(toIndex, 0, moved)
      return updated
    })
  }

  const handleHeaderDragEnd = () => {
    setDraggingColumnId(null)

    if (typeof window !== "undefined") {
      const order = columns.map((column) => column.id)
      setColumnOrder(order)
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(order))
    }
  }

  return {
    columns,
    draggingColumnId,
    dragHandlers: {
      onDragStart: handleHeaderDragStart,
      onDragOver: handleHeaderDragOver,
      onDragEnd: handleHeaderDragEnd,
    },
  }
}
